export function TermsConditionsContent() {
  return (
    <div className="space-y-6 text-[14px] leading-[22px] text-[#051923]">
      <div className="text-center">
        <h1 className="text-[20px] font-semibold">SUBSCRIPTION TERMS &amp; CONDITIONS</h1>
        <p className="mt-1 text-[14px] text-gray-600">Optimal KYC &amp; Payments Limited</p>
        <p className="mt-1 text-[14px] text-gray-600">January 2026</p>
      </div>

      <p>
        These Terms and Conditions govern access to and use of the Optimal KYC platform by business
        and professional customers and apply where the Client accesses the Platform in the course of
        its trade, profession or business.
      </p>

      {/* 1. Definitions and Interpretation */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold">1. Definitions and Interpretation</h2>

        <p>
          <strong>&ldquo;Affiliate&rdquo;</strong> means any entity that directly or indirectly
          controls, is controlled by, or is under common control with a party, where control means
          ownership of more than fifty per cent (50%) of the voting interests or the ability to
          direct management.
        </p>

        <p>
          <strong>&ldquo;API&rdquo;</strong> means any application programming interface or
          integration mechanism made available by Optimal KYC to enable programmatic access to the
          Platform or Services.
        </p>

        <p>
          <strong>&ldquo;Authorised Use&rdquo;</strong> means your use of the Licensed Materials,
          including any Personal Data contained in them, strictly for your internal business and
          compliance purposes and only to:
        </p>
        <ol className="list-[lower-alpha] space-y-1 pl-6">
          <li>
            comply with applicable anti-money laundering, counter-terrorist financing, sanctions,
            and know-your-customer obligations;
          </li>
          <li>
            carry out customer, client, employee, or counterparty identification, verification, due
            diligence, enhanced due diligence, screening, monitoring, and risk assessment;
          </li>
          <li>to support prevent, detect, investigate, and;</li>
          <li>
            exercise or comply with any legal or regulatory right or obligation arising in
            connection with your business or employment activities.
          </li>
        </ol>

        <p>
          <strong>&ldquo;Authorised Users&rdquo;</strong> means employees, workers, contractors or
          consultants of the Client or its Affiliates authorised to access and use the Platform on
          the Client&apos;s behalf.
        </p>

        <p>
          <strong>&ldquo;Client&rdquo;</strong> means the business or professional customer using
          the Platform in the course of its trade, profession or business.
        </p>

        <p>
          <strong>&ldquo;Confidential Information&rdquo;</strong> means any information, in any
          form, whether oral, written, electronic, or otherwise, that is disclosed by or on behalf
          of a party or any of its Affiliates and that:
        </p>
        <ol className="list-[lower-alpha] space-y-1 pl-6">
          <li>is marked or stated to be confidential; or</li>
          <li>
            by its nature or the circumstances of disclosure ought reasonably to be understood as
            confidential.
          </li>
        </ol>
        <p>
          In our case, Confidential Information includes, without limitation, the structure,
          organisation, logic, arrangement, and content of the Licensed Materials, the methods,
          processes, workflows, and systems used to create, operate, maintain, and secure them, and
          any non-public information relating to our technology, platform design, compliance
          methodologies, data models, screening logic, risk frameworks, audit trails, pricing, and
          product roadmap.
        </p>
        <p>Confidential Information does not include information that:</p>
        <ol className="list-[lower-alpha] space-y-1 pl-6">
          <li>is or becomes publicly available other than through a breach of this Agreement;</li>
          <li>was lawfully known to the receiving party without restriction before disclosure;</li>
          <li>
            is lawfully received from a third party without breach of any confidentiality
            obligation;
          </li>
          <li>
            is independently developed by the receiving party without use of or reference to the
            Confidential Information; or
          </li>
          <li>
            is required to be disclosed by law, regulation, court order, or a competent regulatory
            or supervisory authority, provided that (where legally permitted) the receiving party
            gives prompt notice and cooperates to limit the scope of such disclosure.
          </li>
        </ol>

        <p>
          <strong>&ldquo;Data Protection Laws&rdquo;</strong> means all applicable laws and
          regulations relating to the processing of Personal Data and privacy, as amended or
          replaced from time to time, including:
        </p>
        <ol className="list-[lower-alpha] space-y-1 pl-6">
          <li>the UK General Data Protection Regulation (UK GDPR);</li>
          <li>the Data Protection Act 2018;</li>
          <li>
            the retained EU law version of Regulation (EU) 2016/679 (EU GDPR), to the extent
            applicable;
          </li>
          <li>the Privacy and Electronic Communications (EC Directive) Regulations 2003; and</li>
          <li>
            any guidance, codes of practice, or binding decisions issued by a competent supervisory
            authority in each case as applicable to the processing of Personal Data under this
            Agreement.
          </li>
        </ol>

        <p>
          <strong>&ldquo;Documentation&rdquo;</strong> means user guides, onboarding materials,
          release notes and usage policies made available by Optimal KYC.
        </p>

        <p>
          <strong>&ldquo;Effective Date&rdquo;</strong> means the date on which the Client first
          accesses or uses the Platform, or otherwise agrees to be bound by these Terms.
        </p>

        <p>
          <strong>&ldquo;Fees&rdquo;</strong> means the charges payable by the Client for use of the
          Platform, including pre-paid amounts; pay-as-you-go charges; licence fees (where
          applicable), and any other amounts specified or notified by Optimal KYC from time to time.
        </p>

        <p>
          <strong>&ldquo;Licensed Materials&rdquo;</strong> means the Platform, Services,
          Documentation, and any related software, interfaces, tools, workflows, compliance
          methodologies, models, templates, data structures, and other materials made available by
          Optimal KYC to the Client under these Terms, whether accessed via the Platform, API (where
          applicable), or otherwise, but excluding Client Personal Data and Work Product.
        </p>

        <p>
          <strong>&ldquo;Licence Subscription&rdquo;</strong> means access to the Platform purchased
          for a fixed minimum term of twelve (12) months, subject to auto-renewal in accordance with
          these Terms.
        </p>

        <p>
          <strong>&ldquo;Licence Term&rdquo;</strong> means a twelve (12) month subscription period
          commencing on the Effective Date or such other start date agreed in writing, subject to
          auto-renewal in accordance with these Terms.
        </p>

        <p>
          <strong>&ldquo;Personal Data&rdquo;</strong> has the meaning given to it in the Data
          Protection Laws.
        </p>

        <p>
          <strong>&ldquo;Platform&rdquo;</strong> means the Optimal KYC automated compliance support
          tool, including its hosted application, interfaces, AI components and underlying
          infrastructure.
        </p>

        <p>
          <strong>&ldquo;Services&rdquo;</strong> means access to the Platform and related support,
          maintenance and updates.
        </p>

        <p>
          <strong>&ldquo;Terms&rdquo;</strong> means these terms and conditions, as amended from
          time to time.
        </p>

        <p>
          <strong>&ldquo;Third Party Materials&rdquo;</strong> means content, data, software or
          services provided by third parties and made available via the Platform.
        </p>

        <p>
          <strong>&ldquo;Usage Cap&rdquo;</strong> means any limit on the number of searches,
          reports, queries or requests that may be made by the Client within a specified billing
          period, as applicable to the Client&apos;s selected pricing plan or licence.
        </p>

        <p>
          <strong>&ldquo;Work Product&rdquo;</strong> means reports and outputs generated by the
          Platform for the Client&apos;s internal business purposes.
        </p>
      </section>

      {/* 2. Status and Purpose of the Platform */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold">2. Status and Purpose of the Platform</h2>
        <p>
          The Platform is an automated compliance support tool designed to assist Clients with
          customer due diligence and enhanced due diligence processes. It does not make decisions
          and does not replace the Client&apos;s own legal, regulatory or professional obligations.
        </p>
      </section>

      {/* 3. Regulatory Responsibility and Human Oversight */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold">
          3. Regulatory Responsibility and Human Oversight
        </h2>
        <p>
          The Client remains solely responsible for compliance with all applicable AML, KYC and
          professional regulatory requirements, including those imposed by professional bodies,
          supervisory authorities and applicable legislation. Use of the Platform does not
          constitute outsourcing or delegation of regulatory responsibility.
        </p>

        <h3 className="text-[15px] font-semibold">3.1 Decision Support Only (No Sole Reliance)</h3>
        <p>
          The Client acknowledges that any Work Product generated by the Platform is provided as
          decision-support information only. The Client must apply its own professional judgment
          and, where appropriate, conduct additional checks, enquiries, or verification before
          making any compliance, onboarding, transactional, or risk-based decision.
        </p>

        <h3 className="text-[15px] font-semibold">3.2 Automated Outputs and Data Limitations</h3>
        <p>
          The Client acknowledges that Platform outputs are generated through automated processing
          of available data sources at the time of the request. Such outputs may be incomplete,
          time-limited, subject to update, or dependent on source availability and do not represent
          a complete, definitive, or guaranteed assessment of any individual, entity, or risk. The
          presence or absence of any indicator, reference, or result must not be treated as
          conclusive or determinative for any purpose.
        </p>

        <h3 className="text-[15px] font-semibold">3.3 No Outsourcing of Regulated Activities</h3>
        <p>
          Where the Client is authorised or regulated by a Regulatory Body, the Client acknowledges
          that use of the Platform does not constitute outsourcing of regulated activities or
          delegation of professional obligations. Optimal KYC does not provide legal, regulatory, or
          compliance advice and does not assume responsibility for the Client&apos;s compliance with
          applicable laws, professional rules, codes of conduct, or regulatory requirements. All
          professional judgment, regulatory decision-making, and accountability remain solely with
          the Client.
        </p>

        <h3 className="text-[15px] font-semibold">3.4 Mandatory Human Review</h3>
        <p>
          All Work Product must be reviewed and assessed by appropriately trained personnel before
          being relied upon. No Work Product may be used as the sole basis for any decision
          producing legal effects or other similarly significant impacts on an individual or entity.
        </p>
      </section>

      {/* 4. Grant of Licence and Acceptable Use */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold">4. Grant of Licence and Acceptable Use</h2>
        <p>
          Subject to payment of applicable fees, Optimal KYC grants the Client a non-exclusive,
          non-transferable, non-sublicensable, revocable licence to permit Authorised Users to
          access and use the Platform and Documentation solely for lawful internal compliance
          purposes.
        </p>
        <p>
          The Client must ensure a valid lawful basis exists for all Personal Data submitted. The
          Platform should be utilised in a manner that respects its intended purpose, avoiding
          activities such as reverse engineering, extracting models, or misusing AI components. It
          is important to refrain from using the Platform for consumer profiling, employment
          screening, credit scoring, or surveillance. Furthermore, the software should not be
          outsourced to external entities involved in activities such as financial recovery,
          investigative services, or any other operations that fall outside the intended compliance
          framework.
        </p>
      </section>

      {/* 5. Data Processing */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold">5. Data Processing</h2>

        <h3 className="text-[15px] font-semibold">5.1 Roles of the Parties</h3>
        <p>
          For the purposes of the UK GDPR and EU GDPR, the Customer acts as the Data Controller and
          Optimal KYC acts as the Data Processor in respect of any Personal Data processed by
          Optimal KYC on the Customer&apos;s behalf under this Agreement.
        </p>

        <h3 className="text-[15px] font-semibold">5.2 Scope of Processing</h3>
        <p>
          Optimal KYC shall process Personal Data solely on the documented instructions of the
          Customer and only to the extent necessary to provide the services and Licensed Materials,
          including for AML/KYC Compliance Use, unless required to do otherwise by applicable law,
          in which case Optimal KYC shall inform the Customer of that legal requirement unless
          prohibited from doing so.
        </p>

        <h3 className="text-[15px] font-semibold">5.3 Nature and Purpose of Processing</h3>
        <p>
          Processing activities may include the collection, organisation, structuring, analysis,
          screening, verification, monitoring, temporary retention, and deletion of Personal Data
          strictly for the purposes of:
        </p>
        <ol className="list-[lower-alpha] space-y-1 pl-6">
          <li>generating AML/KYC compliance reports at the Customer&apos;s request;</li>
          <li>customer, client, employee, or counterparty identification and verification;</li>
          <li>customer due diligence and enhanced due diligence;</li>
          <li>sanctions, politically exposed persons (PEP), adverse media, and risk screening;</li>
          <li>ongoing monitoring and creation of audit trails; and</li>
          <li>
            compliance with applicable anti-money laundering, counter-terrorist financing,
            sanctions, and related regulatory obligations.
          </li>
        </ol>
        <p>
          Personal Data is processed solely for the purposes set out above and is automatically
          deleted after ten (10) days, after which only a non-personal, non-identifiable audit log
          of platform activity is retained.
        </p>

        <h3 className="text-[15px] font-semibold">
          5.4 Categories of Data Subjects and Personal Data
        </h3>
        <p>
          Data Subjects may include customers, clients, employees, beneficial owners, directors,
          officers, authorised signatories, agents, and counterparties and related businesses.
          Personal Data may include identification data, contact details, professional information,
          ownership and control information, screening results, and risk indicators, and may include
          special category data or criminal offence data where permitted by law and strictly
          required for AML/KYC purposes.
        </p>

        <h3 className="text-[15px] font-semibold">5.5 Confidentiality</h3>
        <p>
          Optimal KYC shall ensure that all persons authorised to process Personal Data are subject
          to appropriate confidentiality obligations, whether contractual or statutory.
        </p>

        <h3 className="text-[15px] font-semibold">5.6 Security Measures</h3>
        <p>
          Optimal KYC shall implement appropriate technical and organisational measures to protect
          Personal Data against accidental or unlawful destruction, loss, alteration, unauthorised
          disclosure, or access, taking into account the nature of the processing, the risks
          involved, and industry best practice for compliance platforms.
        </p>

        <h3 className="text-[15px] font-semibold">5.7 Sub-processing</h3>
        <p>
          The Customer authorises Optimal KYC to engage sub-processors for the provision of the
          services, including cloud infrastructure and AI service providers, provided that:
        </p>
        <ol className="list-[lower-alpha] space-y-1 pl-6">
          <li>
            any sub-processor is bound by written obligations no less protective than those set out
            in this Clause 5; and
          </li>
          <li>
            Optimal KYC remains responsible for the performance of the sub-processor&apos;s
            obligations relating to Personal Data processing.
          </li>
        </ol>

        <h3 className="text-[15px] font-semibold">5.8 International Transfers</h3>
        <p>
          Where Personal Data is transferred outside the UK or EEA, Optimal KYC shall ensure that
          appropriate safeguards are in place in accordance with Data Protection Laws, including the
          use of standard contractual clauses or equivalent lawful transfer mechanisms where
          required.
        </p>

        <h3 className="text-[15px] font-semibold">5.9 Assistance to the Customer</h3>
        <p>
          Taking into account the nature of the processing and acknowledging Optimal KYC 10 day
          deletion policy, we shall provide reasonable assistance to the Customer in:
        </p>
        <ol className="list-[lower-alpha] space-y-1 pl-6">
          <li>responding to data subject rights requests; and</li>
          <li>
            complying with obligations relating to security, personal data breach notification, data
            protection impact assessments, and prior consultation with supervisory authorities.
          </li>
        </ol>

        <h3 className="text-[15px] font-semibold">5.10 Personal Data Breach</h3>
        <p>
          Optimal KYC shall notify the Customer without undue delay upon becoming aware of a
          Personal Data breach and shall provide reasonable information and assistance to enable the
          Customer to meet its legal obligations.
        </p>

        <h3 className="text-[15px] font-semibold">5.11 Deletion and Retention of Data</h3>
        <p>
          Upon termination or expiry of this Agreement, and subject to applicable legal retention
          requirements, Personal Data processed on behalf of the Customer shall be deleted.
          Non-personal, non-identifiable audit logs may be retained for compliance, security, and
          integrity purposes.
        </p>

        <h3 className="text-[15px] font-semibold">
          5.12 Protection of Platform Structure, Methodologies, and Sources
        </h3>
        <p>
          Except where disclosure is strictly required by applicable law, regulation, court order,
          or a competent supervisory or regulatory authority, the Customer must not disclose or make
          available any information relating to the structure, operation, logic, methodologies, or
          underlying data sources of the Platform in a manner that would compromise the Platform,
          its integrity, or its suppliers.
        </p>
        <p>
          Nothing in this Clause 5 restricts the Customer from complying with lawful data subject
          rights or regulatory obligations using its own records, assessments, and compliance
          determinations, provided that such compliance does not require disclosure of Optimal
          KYC&apos;s proprietary platform structure, methodologies, or sources.
        </p>
      </section>

      {/* 6. AI Systems and Infrastructure */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold">6. AI Systems and Infrastructure</h2>
        <p>
          The Platform is designed, developed, and controlled by Optimal KYC and is hosted within
          the European Union using enterprise-grade cloud and artificial intelligence services
          provided by third-party infrastructure providers acting as sub-processors.
        </p>
        <p>
          Artificial intelligence components within the Platform are used solely to support
          compliance workflows, data analysis, and report generation at the Client&apos;s request.
          The Platform is designed as a decision-support system and does not perform automated
          decision-making producing legal or similarly significant effects.
        </p>
        <p>
          Personal Data, Client inputs, prompts, and outputs are processed solely to provide the
          services in accordance with the Client&apos;s documented instructions and this Agreement.
          Such data is not used to train, fine-tune, validate, or improve generalised, shared, or
          third-party artificial intelligence or machine learning models, nor is it used for the
          benefit of other customers. Client inputs and Personal Data are processed transiently to
          generate requested outputs and are automatically deleted after ten (10) days, after which
          only non-personal audit logs are retained.
        </p>
        <p>
          Optimal KYC retains control over the Platform&apos;s architecture, workflows, logic, and
          compliance methodologies. Appropriate technical and organisational measures are maintained
          to ensure logical separation of Client data, prevent cross-customer access, and protect
          the confidentiality, integrity, and availability of the Platform and its underlying
          systems.
        </p>
      </section>

      {/* 7. Fees, Payments and Billing */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold">7. Fees, Payments and Billing</h2>

        <h3 className="text-[15px] font-semibold">7.1 Pricing Model</h3>
        <p>The Platform is provided on a pay-as-you-go, pre-pay or licenced basis.</p>

        <h3 className="text-[15px] font-semibold">7.1.2 Pay-As-You-Go</h3>
        <p>
          Pay-as-you-go means where the client pays for services or products as they are used and
          charges are incurred at the time of each individual transaction or usage event. Fees will
          be charged per report, query, or other usage event as notified to the Client from time to
          time. Fees are incurred at the point a report or output is requested.
        </p>

        <h3 className="text-[15px] font-semibold">7.1.3 Pay-in-Advance-Pre Pay Searches</h3>
        <p>
          The Client may alternatively purchase searches on a pay-in-advance basis. Pay-in-advance
          means where payment is made upfront with fees deducted as searches are conducted. Searches
          are charged at &pound;25 per full search, unless purchased as part of a discounted search
          bundle.
        </p>
        <p>
          Optimal KYC may offer discounted bundles of searches, including blocks of 25, 50, 100 or
          more searches, with percentage discounts applied to higher volumes as notified at the time
          of purchase.
        </p>
        <p>
          Prepaid searches must be paid for in advance and are deducted as searches are performed.
          Prepaid searches are non-refundable and have no cash value. Unless otherwise stated,
          prepaid searches must be used within the period specified at the time of purchase or,
          where no period is specified, within twelve (12) months of purchase.
        </p>

        <h3 className="text-[15px] font-semibold">7.1.4 Licence Model</h3>
        <p>
          In addition to pay-as-you-go access, Optimal KYC may offer the Platform under a fixed-term
          licence model. Under a licence model, the Client is granted access to the Platform for a
          Licence Term, subject to a Usage Cap and any other applicable restrictions notified at the
          point of purchase. Licence fees may be payable monthly or annually in advance, as selected
          by the Client, and are subject to VAT where applicable. Unless otherwise stated, licence
          subscriptions automatically renew for successive twelve (12) month periods unless the
          Client gives at least sixty (60) days&apos; written notice prior to the end of the current
          Licence Term.
        </p>
        <p>
          Usage under a licence model is subject to the applicable Usage Cap. Any unused searches or
          requests do not roll over between billing periods or Licence Terms unless expressly agreed
          in writing. Where the Client purchases a Licence Subscription, the Client commits to a
          minimum term of twelve (12) months. Licence Subscriptions may not be cancelled for
          convenience during the initial Licence Term. In addition, and during an active Licence
          Term, the Client may not downgrade to pay-as-you-go or pay-in-advance access or terminate
          the Licence Subscription early, save where expressly permitted under Clause 13 for breach
          or legal compliance.
        </p>

        <h3 className="text-[15px] font-semibold">7.3 Payment Method</h3>
        <p>
          All fees are payable by corporate debit or credit card via Optimal KYC&apos;s authorised
          payment processor (currently Stripe), unless otherwise agreed in writing. By submitting
          payment details, the Client authorises Optimal KYC and its payment processor to charge
          applicable fees automatically as usage occurs.
        </p>

        <h3 className="text-[15px] font-semibold">7.4 Invoicing and Receipts</h3>
        <p>
          Transaction receipts and usage confirmations may be provided electronically via the
          Platform or by email. Formal invoices will be issued only where required by law or
          expressly agreed in writing.
        </p>

        <h3 className="text-[15px] font-semibold">7.5 No Refunds</h3>
        <p>
          Fees are non-refundable once a report or output has been generated, regardless of whether
          the Client subsequently relies on or uses the Work Product. This includes licence fees,
          subscription fees, and any prepaid or bundled searches.
        </p>

        <h3 className="text-[15px] font-semibold">7.6 Taxes</h3>
        <p>
          All fees are exclusive of VAT and any other applicable taxes, duties, or charges, which
          shall be payable by the Client in addition where applicable.
        </p>

        <h3 className="text-[15px] font-semibold">7.7 Pricing Changes</h3>
        <p>
          Optimal KYC may update its fees or pricing structure on reasonable notice. Continued use
          of the Platform after such notice constitutes acceptance of the updated pricing.
        </p>
      </section>

      {/* 8. Intellectual Property */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold">8. Intellectual Property</h2>
        <p>
          All intellectual property rights in and to the Platform, Services, Documentation, and all
          underlying software, algorithms, models, workflows, methodologies, user interfaces, system
          architecture, and compliance frameworks (together, the Optimal KYC Materials) are and
          shall remain vested in Optimal KYC.
        </p>
        <p>
          Subject to payment of applicable fees and compliance with these Terms, the Client is
          granted a limited, non-exclusive, non-transferable, non-sublicensable licence to access
          and use the Platform and Documentation during the term solely for its internal business
          and compliance purposes.
        </p>
        <p>
          The Client retains ownership of any Personal Data it submits to the Platform. Subject to
          Clause 5 (Data Processing), Optimal KYC may process such data only to provide the services
          and does not acquire any ownership rights in Client data.
        </p>
        <p>
          Work Product is provided for the Client&apos;s internal business and regulatory engagement
          purposes only. No warranties are given as to the accuracy, completeness, fitness for
          purpose, or regulatory acceptance of any Work Product, and all outputs remain subject to
          human review and professional judgment.
        </p>
        <p>
          Nothing in this Agreement transfers to the Client any intellectual property rights in the
          Platform, artificial intelligence components, models, or underlying technologies, nor does
          it restrict Optimal KYC from developing, improving, or providing similar services or
          technologies to other customers, provided that no Client Personal Data is used for such
          purposes.
        </p>
        <p>
          The Client must not reverse engineer, decompile, disassemble, extract models, derive
          source code, or otherwise attempt to discover or reproduce the underlying structure,
          logic, or methodologies of the Platform, except to the extent expressly permitted by
          applicable law.
        </p>
      </section>

      {/* 9. Confidentiality */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold">9. Confidentiality</h2>

        <h3 className="text-[15px] font-semibold">9.1 Confidentiality Obligations</h3>
        <p>
          Each party shall keep the other party&apos;s Confidential Information confidential and
          shall not disclose it to any third party except as expressly permitted under these Terms.
          Confidential Information may be used solely to perform obligations or exercise rights
          under these Terms and for no other purpose.
        </p>

        <h3 className="text-[15px] font-semibold">9.2 Protection and Safeguards</h3>
        <p>
          Each party shall apply appropriate technical and organisational measures to protect the
          other party&apos;s Confidential Information against unauthorised access, use, disclosure,
          alteration, or loss. Such measures shall be no less protective than those the receiving
          party applies to its own confidential information of a similar nature.
        </p>

        <h3 className="text-[15px] font-semibold">9.3 Permitted Disclosures</h3>
        <p>Confidential Information may be disclosed by the receiving party:</p>
        <ol className="list-[lower-alpha] space-y-1 pl-6">
          <li>
            to its employees, officers, contractors, professional advisers, auditors, insurers, or
            Affiliates on a strict need-to-know basis and subject to confidentiality obligations no
            less protective than those set out in these Terms;
          </li>
          <li>
            where disclosure is required by applicable law, regulation, court order, or a competent
            regulatory or supervisory authority, provided that (where legally permitted) the
            receiving party gives prompt notice and cooperates to limit the scope of such
            disclosure; or
          </li>
          <li>with the prior written consent of the disclosing party.</li>
        </ol>

        <h3 className="text-[15px] font-semibold">9.4 Exclusions</h3>
        <p>Confidential Information does not include information that:</p>
        <ol className="list-[lower-alpha] space-y-1 pl-6">
          <li>is or becomes publicly available other than through a breach of these Terms;</li>
          <li>was lawfully known to the receiving party without restriction before disclosure;</li>
          <li>
            is lawfully received from a third party without breach of any confidentiality
            obligation; or
          </li>
          <li>
            is independently developed without use of or reference to the Confidential Information.
          </li>
        </ol>

        <h3 className="text-[15px] font-semibold">
          9.5 Regulatory and Data Protection Disclosures
        </h3>
        <p>
          Nothing in these Terms prevents either party from complying with lawful regulatory,
          supervisory, or data protection obligations, including responding to data subject rights
          requests or regulatory enquiries, provided that such compliance does not require
          disclosure of the other party&apos;s proprietary systems, methodologies, or Confidential
          Information beyond what is strictly required.
        </p>

        <h3 className="text-[15px] font-semibold">9.6 Survival</h3>
        <p>
          The confidentiality obligations in this Clause 9 shall survive termination or expiry of
          these Terms for a period of five (5) years, or indefinitely in respect of trade secrets
          and proprietary technical information.
        </p>
      </section>

      {/* 10. Security, Passwords and Access Controls */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold">10. Security, Passwords and Access Controls</h2>
        <p>
          The Client is responsible for managing Authorised Users and ensuring that access to the
          Platform is limited to individuals who require such access for legitimate business and
          compliance purposes. The Client must ensure that all access credentials are kept
          confidential, are not shared, and are used only by the individual to whom they are issued.
        </p>
        <p>
          The Client must promptly remove or disable access for any Authorised User who no longer
          requires access or who leaves the Client&apos;s organisation. The Client remains
          responsible for all activity carried out using its credentials or by its Authorised Users.
        </p>
        <p>
          Optimal KYC implements appropriate technical and organisational measures to protect the
          security and integrity of the Platform. Without limiting any other rights, Optimal KYC may
          suspend, restrict, or terminate access to the Platform, in whole or in part, where
          reasonably necessary to protect security, prevent misuse, comply with applicable law or
          regulatory requirements, address suspected unauthorised access, or mitigate risk to the
          Platform or other customers.
        </p>
        <p>
          Optimal KYC shall not be responsible for any loss, damage, or unauthorised activity
          arising from the Client&apos;s failure to comply with this Clause 10 or to maintain
          appropriate internal access controls.
        </p>
      </section>

      {/* 11. Systems Requirements */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold">11. Systems Requirements</h2>
        <p>
          The Client is responsible for procuring, maintaining, and securing all hardware, software,
          browsers, networks, internet connections, and internal security controls required to
          access and use the Platform.
        </p>
        <p>
          The Client must ensure that its systems and environments are reasonably compatible with
          the Platform and comply with any minimum requirements or usage guidance made available by
          Optimal KYC from time to time.
        </p>
        <p>
          Optimal KYC is not responsible for any inability to access or use the Platform arising
          from the Client&apos;s systems, network configuration, security settings, or third-party
          software or services.
        </p>
      </section>

      {/* 12. Support and Updates */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold">12. Support and Updates</h2>
        <p>
          Support for the Platform is provided during standard business hours and in accordance with
          any support guidance or service descriptions made available by Optimal KYC from time to
          time. Support is limited to assistance with access to and general use of the Platform and
          does not include legal, regulatory, or compliance advice.
        </p>
        <p>
          The Platform is provided on an &ldquo;as available&rdquo; basis. Optimal KYC does not
          guarantee uninterrupted or error-free operation and may suspend or restrict access
          temporarily for maintenance, security, updates, or compliance purposes.
        </p>
        <p>
          The Client has the option to purchase expert advice following a full search. To submit a
          request the software will guide the Client on the process for this.
        </p>
        <p>
          Optimal KYC may implement updates, modifications, or enhancements to the Platform
          automatically, including updates to functionality, data sources, security measures, and
          artificial intelligence components, provided that such updates do not materially reduce
          the overall functionality of the Platform.
        </p>
      </section>

      {/* 13. Term and Termination */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold">13. Term and Termination</h2>

        <p>
          <strong>13.1</strong> These Terms commence on the Effective Date and remain in force
          unless and until terminated in accordance with this Clause 13.
        </p>

        <h3 className="text-[15px] font-semibold">
          13.2 Licence Subscriptions &ndash; Minimum Term
        </h3>
        <p>
          Where the Client has purchased access to the Platform under a Licence Subscription, these
          Terms shall remain in force for the duration of the Licence Term. The Client commits to a
          minimum initial term of twelve (12) months and may not terminate the Licence Subscription
          for convenience during the initial Licence Term.
        </p>

        <h3 className="text-[15px] font-semibold">
          13.3 Licence Subscriptions &ndash; Auto-Renewal and Notice
        </h3>
        <p>
          Licence Subscriptions automatically renew for successive twelve (12) month periods unless
          either party gives not less than sixty (60) days&apos; written notice prior to the end of
          the then-current Licence Term. Any renewal shall be on the same licence basis unless
          otherwise agreed in writing.
        </p>

        <h3 className="text-[15px] font-semibold">13.4 Pay-As-You-Go Access</h3>
        <p>
          Where the Client accesses the Platform solely on a pay-as-you-go basis, these Terms remain
          in force unless and until terminated in accordance with this Clause 13. The Client may
          cease using the Platform at any time without notice.
        </p>
        <p>
          Optimal KYC may suspend or terminate pay-as-you-go access on reasonable notice, or
          immediately where required to comply with applicable law, protect security, prevent
          misuse, or address non-payment.
        </p>

        <h3 className="text-[15px] font-semibold">13.5 Effect of Termination</h3>
        <p>
          Termination or expiry of these Terms, or cessation of access to the Platform, shall not
          affect any fees incurred prior to termination, nor any provisions which by their nature
          are intended to survive termination, including provisions relating to intellectual
          property, confidentiality, data protection, liability, indemnities, audit rights, and
          governing law.
        </p>
      </section>

      {/* 14. Liability and Responsibility */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold">14. Liability and Responsibility</h2>
        <p>
          The Platform is provided as a compliance support and decision-assistance tool only. The
          Client remains solely responsible for all compliance, onboarding, transactional, and
          regulatory decisions made using or in reliance on the Platform or any Work Product.
        </p>
        <p>
          To the maximum extent permitted by law, Optimal KYC shall not be liable for any loss,
          damage, cost, claim, fine, penalty, enforcement action, regulatory sanction, reputational
          harm, or adverse outcome arising from:
        </p>
        <ol className="list-[lower-alpha] space-y-1 pl-6">
          <li>the Client&apos;s use of, or reliance on, the Platform or any Work Product;</li>
          <li>
            any compliance, onboarding, transactional, or risk-based decision made by the Client;
          </li>
          <li>
            the Client&apos;s failure to carry out independent checks, verification, or professional
            judgment; or
          </li>
          <li>
            the Client&apos;s breach of applicable laws, regulations, professional rules, or
            supervisory requirements.
          </li>
        </ol>
        <p>
          Subject to the above, and to the extent that liability cannot be excluded as a matter of
          law, Optimal KYC&apos;s total aggregate liability arising out of or in connection with
          these Terms (whether in contract, tort (including negligence), breach of statutory duty,
          or otherwise) shall be limited to the total fees actually paid by the Client to Optimal
          KYC in the three (3) months preceding the event giving rise to the claim.
        </p>
        <p>
          Nothing in these Terms excludes or limits liability for death or personal injury caused by
          negligence, fraud, or fraudulent misrepresentation, or any other liability which cannot
          lawfully be excluded.
        </p>
      </section>

      {/* 15. Indemnities */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold">15. Indemnities</h2>

        <h3 className="text-[15px] font-semibold">15.1 Intellectual Property Indemnity</h3>
        <p>
          Optimal KYC shall defend the Client against any third-party claim alleging that the
          authorised use of the Platform in accordance with these Terms infringes a United Kingdom
          registered intellectual property right, and shall indemnify the Client against any direct
          damages and costs finally awarded by a court of competent jurisdiction or agreed in
          settlement, provided that the Client:
        </p>
        <ol className="list-[lower-alpha] space-y-1 pl-6">
          <li>promptly notifies Optimal KYC of the claim;</li>
          <li>gives Optimal KYC sole control over the defence and settlement of the claim; and</li>
          <li>provides reasonable cooperation at Optimal KYC&apos;s expense.</li>
        </ol>

        <h3 className="text-[15px] font-semibold">15.2 Exclusions</h3>
        <p>
          Optimal KYC shall have no liability or obligation under this Clause 15 to the extent that
          any claim arises from:
        </p>
        <ol className="list-[lower-alpha] space-y-1 pl-6">
          <li>
            use of the Platform other than in accordance with these Terms or the Documentation;
          </li>
          <li>
            modification of the Platform or Work Product by any person other than Optimal KYC;
          </li>
          <li>
            combination of the Platform with any software, system, data, or service not provided or
            approved by Optimal KYC;
          </li>
          <li>
            use of third-party data, content, or sources supplied by or on behalf of the Client; or
          </li>
          <li>
            compliance, onboarding, transactional, or regulatory decisions made by the Client.
          </li>
        </ol>

        <h3 className="text-[15px] font-semibold">15.3 Regulatory and Compliance Matters</h3>
        <p>
          For the avoidance of doubt, Optimal KYC does not indemnify the Client in respect of, and
          shall have no liability for, any regulatory fines, penalties, enforcement action,
          supervisory measures, professional discipline, or adverse compliance outcome arising from
          the Client&apos;s use of the Platform or reliance on any Work Product. All such risk and
          responsibility remains solely with the Client.
        </p>

        <h3 className="text-[15px] font-semibold">15.4 Client Indemnity</h3>
        <p>
          The Client shall indemnify and hold harmless Optimal KYC against any claims, losses,
          liabilities, costs, and expenses arising from:
        </p>
        <ol className="list-[lower-alpha] space-y-1 pl-6">
          <li>the Client&apos;s breach of these Terms;</li>
          <li>the Client&apos;s misuse of the Platform or Work Product;</li>
          <li>
            the Client&apos;s failure to comply with applicable laws, regulations, or professional
            obligations; or
          </li>
          <li>
            Personal Data or other content submitted by the Client in breach of Data Protection Laws
            or third-party rights.
          </li>
        </ol>

        <h3 className="text-[15px] font-semibold">15.5 Remedies</h3>
        <p>
          If the Platform becomes, or in Optimal KYC&apos;s reasonable opinion is likely to become,
          subject to a claim under Clause 15.1, Optimal KYC may, at its option and expense:
        </p>
        <ol className="list-[lower-alpha] space-y-1 pl-6">
          <li>procure the right for the Client to continue using the Platform;</li>
          <li>modify or replace the Platform so that it becomes non-infringing; or</li>
          <li>terminate the affected services and refund any unused prepaid fees (if any).</li>
        </ol>
        <p>
          This Clause 15 states the Client&apos;s sole and exclusive remedies for claims of
          intellectual property infringement relating to the Platform.
        </p>
      </section>

      {/* 16. Audit and Contractual Compliance Verification */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold">
          16. Audit and Contractual Compliance Verification
        </h2>
        <p>
          Optimal KYC may, where reasonably necessary, verify the Client&apos;s compliance with
          these Terms for the purposes of maintaining security, preventing misuse, enforcing
          authorised use restrictions, meeting legal or regulatory obligations applicable to Optimal
          KYC, and resolving compliance or billing issues.
        </p>
        <p>
          Any such verification shall be limited in scope and proportionate and may include requests
          for reasonable information from the Client relating to authorised users, access controls,
          usage patterns, or lawful basis for processing. Optimal KYC shall not require disclosure
          of the Client&apos;s confidential compliance assessments, regulated decision-making, or
          internal risk determinations.
        </p>
        <p>
          Verification under this Clause 16 does not involve routine monitoring of Client inputs,
          substantive review of Work Product, or assessment of Client compliance outcomes.
        </p>
      </section>

      {/* 17. Operational and Technical Monitoring */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold">17. Operational and Technical Monitoring</h2>
        <p>
          Optimal KYC may carry out continuous operational and technical monitoring of access to and
          use of the Platform for system administration purposes only, including maintaining
          security, ensuring system integrity and availability, preventing misuse, validating usage
          for billing accuracy, and supporting service performance.
        </p>
        <p>
          Such monitoring is limited to technical, operational, and security metrics and does not
          involve routine review of Client inputs, Personal Data, compliance assessments, or
          decision-making activities unless requested to do so by a Client who requests expert
          advice.
        </p>
      </section>

      {/* 18. Assignment & Subcontracting */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold">18. Assignment &amp; Subcontracting</h2>
        <p>
          Optimal KYC &amp; Client may assign, novate, or transfer these Terms, in whole or in part,
          to an Affiliate or in connection with a merger, acquisition, corporate reorganisation,
          change of control, or sale of all or substantially all of its assets, upon written notice
          to the Client.
        </p>
        <p>
          Nothing in this Clause 18 prevents Optimal KYC from subcontracting any part of the
          services, provided that Optimal KYC remains responsible for the performance of its
          obligations under these Terms in accordance with Clause 5 (Data Processing).
        </p>
      </section>

      {/* 19. Notices */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold">19. Notices</h2>
        <p>
          Any notice given under these Terms must be in writing and may be delivered by hand, sent
          by pre-paid post, or sent by email to the address or email address notified by the
          receiving party for this purpose.
        </p>
        <p>Notices shall be deemed received:</p>
        <ol className="list-[lower-alpha] space-y-1 pl-6">
          <li>if delivered by hand, at the time of delivery;</li>
          <li>if sent by post, seven (7) business days after posting; and</li>
          <li>
            if sent by email, within 48 hours of transmission, provided no delivery failure notice
            is received.
          </li>
        </ol>
        <p>
          This Clause does not apply to routine operational communications, platform notifications,
          support messages, or billing confirmations, which may be provided electronically via the
          Platform or by email.
        </p>
      </section>

      {/* 20. Limitation Period */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold">20. Limitation Period</h2>
        <p>
          Any claim, action, or proceeding arising out of or in connection with these Terms (whether
          in contract, tort (including negligence), breach of statutory duty, or otherwise) must be
          commenced within six (6) months of the date on which the party bringing the claim first
          became aware, or ought reasonably to have become aware, of the circumstances giving rise
          to the claim.
        </p>
      </section>

      {/* 21. Governing Law and Jurisdiction */}
      <section className="space-y-3">
        <h2 className="text-[16px] font-semibold">21. Governing Law and Jurisdiction</h2>
        <p>
          These Terms and any dispute or claim arising out of or in connection with them (including
          non-contractual disputes or claims) are governed by and construed in accordance with the
          laws of England and Wales. The courts of England and Wales shall have exclusive
          jurisdiction to settle any such dispute or claim.
        </p>
      </section>
    </div>
  );
}
